<template>
  <div class="result">
    <div class="receipt-container">
      <notes-sidebar />
      <multipane
        class="custom-resizer"
        layout="vertical"
        @paneResize="onResize"
      >
        <div
          :style="{ width: leftPaneWidth + 'px', maxWidth: '600px', minWidth: '500px' }"
          class="pane"
        >
          <div class="pane-content">
            <supplements-left-panel
              :rsGroups="rsGroups"
              :brandSets="brandSets"
              :loading="loading"
              :highlightedSupplementId="highlightedSupplementId"
              title="Supplement Recommender"
              @open-settings="openSettingsDialog"
              @show-crg-notes="handleCrgNotesClick"
              @warning-notes="warningNotes"
              @supplement-toggled="onSupplementToggled"
              @group-selection-changed="onGroupSelectionChanged"
              @supplements-reordered="onSupplementsReordered"
              @supplement-selected="onSelect"
              @supplement-dragged="onDragStart"
              @supplement-highlight="onSupplementHighlight"
              @supplement-unhighlight="onSupplementUnhighlight"
            />
          </div>
        </div>

        <multipane-resizer>
          <div class="center-line"></div>
        </multipane-resizer>

        <div class="pane right-panel" style="flexGrow: 1">
          <div class="pane-top">
            <p class="pane-title">Supplement Schedule</p>
            <div class="pricing-summary">
              <div class="pricing-item">
                <span class="pricing-label">Daily: </span>
                <span class="pricing-value">
                  {{ results?.record?.dailyPrice !== 0 && results?.record?.dailyPrice != null
                  ? '$' + results.record.dailyPrice.toFixed(2)
                  : ''
                  }}
                </span>
              </div>
              <div class="pricing-item">
                <span class="pricing-label">Weekly: </span>
                <span class="pricing-value">
                  {{ results?.record?.weeklyPrice !== 0 && results?.record?.weeklyPrice != null
                  ? '$' + results.record.weeklyPrice.toFixed(2)
                  : ''
                  }}
                </span>
              </div>
              <div class="pricing-item">
                <span class="pricing-label">Monthly: </span>
                <span class="pricing-value">
                  {{ results?.record?.monthlyPrice !== 0 && results?.record?.monthlyPrice != null
                  ? '$' + results.record.monthlyPrice.toFixed(2)
                  : ''
                  }}
                </span>
              </div>
              <div class="pricing-item total">
                <span class="pricing-label">Total:</span>
                <span class="pricing-value">
                   <span class="pricing-value">
                  {{ results?.record?.totalPrice !== 0 && results?.record?.totalPrice != null
                     ? '$' + results.record.totalPrice.toFixed(2)
                     : ''
                     }}
                </span>
                </span>
              </div>
            </div>
          </div>
          <div class="pane-content"
               @drop.prevent="onDrop"
               @dragover.prevent
          >
            <supplement-details
              :headers="headers"
              :items="items"
              :orderMethod="orderMethod"
              :highlightedSupplementId="highlightedSupplementId"
              :totals="dosageTotals"
              @warning-notes="warningNotes"
              @supplement-highlight="onSupplementHighlight"
              @supplement-unhighlight="onSupplementUnhighlight"
              @show-crg-notes="handleCrgNotesClick"
            />
          </div>
        </div>
      </multipane>
    </div>
    <notes-dialog v-if="dialogs.notesDialog.isOpen" @showPopup="showPopup" />
    <modal-supplement-vendor-selection
      v-if="dialogs.modalSupplementVendorSelection.isOpen"
      :brands-data="brandsData"
      @apply="handleBrandVendorSelection"
    />
    <modal-crg-notes v-if="dialogs.modalCrgNotes.isOpen"></modal-crg-notes>
    <div v-if="loading" class="loading d-flex justify-center px-2 py-2 align-center">
      <v-progress-circular
        indeterminate
        color="#008C89"
      ></v-progress-circular>
    </div>
    <div class="custom-alert">
      <v-alert
        color="#7ac1fb"
        dismissible
        type="info"
        v-model="alertIsOpen"
      >
        <span style="font-size: 14px;">{{popupMessage}}</span>
      </v-alert>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { trackEvent } from '@/utils/analytics'
import notesSidebar from '@/components/layouts/notesSidebar'
import NotesDialog from '@/components/layouts/dialogs/notes-dialog'
import SupplementsLeftPanel from '@/components/layouts/supplements/supplements-left-panel'
import SupplementDetails from '@/components/layouts/supplements/supplement-details'
import draggable from 'vuedraggable'
import ModalCrgNotes from '@/components/layouts/dialogs/modal-crg-notes'

export default {
  name: 'results',
  props: {
    orderMethod: {
      type: Object
    },
    isUpdateResults: {
      type: Boolean,
      default: false
    },
  },
  components: {
    NotesDialog,
    ModalCrgNotes,
    ModalSupplementVendorSelection: () => import('@/components/layouts/dialogs/modal-supplement-vendor-selection.vue'),
    notesSidebar,
    draggable,
    SupplementsLeftPanel,
    SupplementDetails
  },
  data: () => ({
    leftPaneWidth: 500,
    selectedSupplements: [],
    selectedSupplement: null,
    draggedItem: null,
    apiResponse: null,
    loading: true,
    brandVendorsData: null,
    brandsData: null,
    highlightedSupplementId: null,
    alertIsOpen: false,
    popupMessage: null,
  }),
  async mounted() {
    this.loading = true
    const {id} = this.$route.params

    const response = await this.getResults({id, orderMethod: null})
    this.apiResponse = response
    this.loading = false

    const record = response.data?.record
    if (!record) return

    if (record.showBrandVendorSelection) {
      await this.fetchAndShowBrandsDialog()
    }

    const initialSelected = []
    const brandSets = record?.brandSets?.$values || []

    brandSets.forEach(brand => {
      brand.groups?.$values?.forEach(group => {
        group.supplements?.$values?.forEach(supplement => {
          if (supplement.selected) {
            initialSelected.push(supplement)
          }
        })
      })
    })

    this.selectedSupplements = initialSelected

    const savedWidth = localStorage.getItem('leftPaneWidth');
    if (savedWidth) {
      this.leftPaneWidth = parseInt(savedWidth, 10);
    }

    this.handleScreenResize()
    window.addEventListener('resize', this.handleScreenResize)
  },

  watch: {
    async 'isUpdateResults' (val) {
      if (val) {
        const { id } = this.$route.params
        const res = await this.getResults({ id, orderMethod: null })
        if (res) {
          const obj = this.patientOrderMethods.find(item => item.code === this.results.record.orderMethodCode)
          // this.getNotes(this.$route.params.id)
          this.$emit('updateOrderMethod', obj)
        }
        this.$emit('updateIsUpdateResults', false)
      }
    },
    totalSelectedCount(newCount) {
      if (newCount !== undefined && newCount !== null) {
        this.$store.dispatch('visits/updateTotalSelectedResultSupplements', {
          totalSelectedResultSupplements: newCount
        });
      }
    }
  },

  computed: {
    ...mapState('results', {
      headers(state) {
        const headers = Object.assign([], state?.results?.headers?.$values)
        headers.push({icon: 'mdi-cog', value: 'actions', isIcon: true, sortable: false})

        const mapped = headers?.map(el => {
          const transformed = {
            ...this._.omit(el, ['skip', '$id', 'width']),
            text: el.value !== 'actions'
              ? el.text?.toUpperCase()?.replace(/ /gi, '<br/>')
              : el.text,
            isReplacingSpace: el.value !== 'actions' ? 1 : 0,
          }
          return transformed
        })

        const filtered = mapped.filter(el => {
          const condition = el.text !== 'USE'
          return condition
        })

        return filtered
      },

      items(state) {
        const updatedValues = state?.results?.record?.resultSupplementGroups?.$values.map(group => {
          const supplements = group.resultSupplements.$values.map(item => {
            const updatedItem = Object.assign(item, {supplementWidth: '200px'})
            return updatedItem
          })

          const updatedGroup = {
            ...group,
            resultSupplements: {
              $id: group.$id,
              $values: supplements
            }
          }
          return updatedGroup
        })

        const items = Object.assign([], updatedValues)
        return items
      }
    }),
    ...mapState({
      patientOrderMethods: state => state.settings.patients.patientOrderMethods.$values.map(item => {
        return {
          ...item,
          text: item.name,
          value: item.code,
          id: item.$id
        }
      }),
      visit: state => state.visits?.visit
    }),
    dialogs() {
      return this.$store.state.dialogs
    },

    brandSets() {
      const setsRaw = this.results?.record?.brandSets?.$values || []

      const mapped = setsRaw.map(brand => ({
        ...brand,
        groups: brand.groups?.$values?.map(group => ({
          ...group,
          supplements: group.supplements?.$values || []
        })) || []
      }))

      return mapped
    },

    rsGroups() {
      return this.results?.record?.rsGroups?.$values || []
    },

    results() {
      return this.$store.state?.results?.results
    },

    totalSelectedCount() {
      return this.results?.record?.totalSelectedResultSupplements
    },

    dosageTotals() {
      const rec = this.results?.record
      if (!rec) return {}

      return {
        totalDosageUponArising: rec.totalDosageUponArising || 0,
        totalDosageBreakfast: rec.totalDosageBreakfast || 0,
        totalDosage10am: rec.totalDosage10am || 0,
        totalDosageLunch: rec.totalDosageLunch || 0,
        totalDosage3pm: rec.totalDosage3pm || 0,
        totalDosageDinner: rec.totalDosageDinner || 0,
        totalDosage7pm: rec.totalDosage7pm || 0,
        totalDosageBeforeSleep: rec.totalDosageBeforeSleep || 0,
        daysSupply: rec.daysSupply || 0
      }
    }
  },

  methods: {
    ...mapActions('results', ['getResults', 'getResultBrands']),
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),

    showPopup ({ res, supplementName }) {
      this.alertIsOpen = true
      this.popupMessage = `Notes for ${supplementName} was updated successfully`
      this.TOGGLE_DIALOG({ name: 'notesDialog', isOpen: false })
      const self = this
      setTimeout(function () {
        self.alertIsOpen = false
      }, 1300)
    },

    warningNotes (item) {
      this.TOGGLE_DIALOG({ name: 'notesDialog', title: 'Special Notes', isOpen: true, data: item })
    },

    handleCrgNotesClick(item) {
      trackEvent(this, 'supplement_info', 'Results', 'Supplement Info', {
        supplement: item.name || item.supplementName
      })

      this.TOGGLE_DIALOG({
        name: 'modalCrgNotes',
        isOpen: true,
        title: item.name || item.supplementName,
        data: item
      })
    },

    async fetchAndShowBrandsDialog() {
      const {id} = this.$route.params

      // if (!this.brandsData) {
      const brandsResponse = await this.getResultBrands(id)
      this.brandsData = brandsResponse?.record
      // }

      this.TOGGLE_DIALOG({
        name: 'modalSupplementVendorSelection',
        isOpen: true
      })
    },

    async openSettingsDialog() {
      await this.fetchAndShowBrandsDialog()
    },

    async handleBrandVendorSelection(brandVendorsData) {
      // this.brandVendorsData = brandVendorsData

      const {id} = this.$route.params
      await this.getResults({
        id,
        orderMethod: this.orderMethod?.code || null,
        brandVendorsData
      })
    },

    onSelect(supplement) {
      this.selectedSupplement = supplement
    },

    onDragStart(supplement) {
      this.draggedItem = supplement
    },

    onDrop() {
      if (this.draggedItem) {
        this.selectedSupplement = this.draggedItem
        this.draggedItem = null
      }
    },

    onSupplementToggled({supplement}) {
      if (supplement.selected) {
        if (!this.selectedSupplements.find(s => s.id === supplement.id)) {
          this.selectedSupplements.push(supplement)
        }
      } else {
        this.selectedSupplements = this.selectedSupplements.filter(s => s.id !== supplement.id)
      }
    },

    onGroupSelectionChanged({brandCode, groupName, selected}) {
      console.log('Group selection changed:', {brandCode, groupName, selected})
    },

    onSupplementsReordered(event) {
      console.log('Supplements reordered:', event)
    },

    handleScreenResize() {
      if (window.innerWidth <= 1050) {
        this.leftPaneWidth = 450
      } else {
        const savedWidth = localStorage.getItem('leftPaneWidth')
        this.leftPaneWidth = savedWidth ? parseInt(savedWidth, 10) : 500
      }
    },

    onResize(pane, container, size) {
      if (pane.classList.contains('pane')) {
        this.leftPaneWidth = size
        if (window.innerWidth > 1050) {
          localStorage.setItem('leftPaneWidth', size)
        }
      }
    },

    debouncedSaveWidth: (() => {
      let timeoutId
      return function (width) {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
          localStorage.setItem('leftPaneWidth', width)
        }, 300)
      }
    })(),

    onSupplementHighlight(supplementId) {
      this.highlightedSupplementId = supplementId
    },

    onSupplementUnhighlight() {
      this.highlightedSupplementId = null
    },
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleScreenResize)
  },
}
</script>

<style lang="scss" scoped>
.multipane > div {
  z-index: unset !important;
}

.result {
  display: flex;
  flex-direction: column;
  height: 100vh;

  .receipt-container {
    flex: 1;
    display: flex;
    overflow: hidden;
  }

  .custom-alert {
    max-width: 300px;
    position: fixed;
    top: 10px;
    right: 20px;
    z-index: 100;
  }

  .pricing-summary {
    display: flex;
    align-items: center;
    column-gap: 24px;
    color: #718096;
    font-size: 13px;
    font-weight: 600;
    line-height: 20px;
    margin-left: auto;

    .pricing-item {
      .pricing-label {
        font-size: 12px;
        font-weight: 400;
      }

      &.total {
        display: flex;
        align-items: center;
        column-gap: 20px;
        margin-left: 6px;

        .pricing-label {
          font-weight: 600;
        }

        .pricing-value {
          color: #1A202C;
          font-size: 15px;
        }
      }
    }
  }
}

.custom-resizer {
  flex: 1;
  display: flex;
  max-width: 100%;
  background-color: #F7FAFC;

  .pane {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    //min-width: 0;

    &:first-child {
      @media (max-width: 1050px) {
        min-width: 450px !important;
        width: 450px !important;
      }
    }
  }

  .pane-top {
    display: flex;
    align-items: center;
    column-gap: 7px;
    margin-bottom: 8px;

    &__inner {
      display: flex;
      align-items: center;
      column-gap: 7px;
      margin: 0;
    }
  }

  .pane-title {
    font-size: 12px;
    font-weight: 500;
    line-height: 32px;
    color: #718096;
    text-transform: uppercase;
    margin-bottom: 0;
  }

  .pane-content {
    flex: 1;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .right-panel {
    min-width: 0;
    overflow-x: auto !important;
  }

  ::v-deep .multipane-resizer {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
    left: 0 !important;
    width: 24px;
    background-color: transparent;
    margin: 0 !important;
    cursor: col-resize;

    &:before,
    &:after {
      content: "";
      position: absolute;
      top: 50%;
      margin-top: -16px;
      height: 33px;
      width: 2px;
      background-color: #E2E8F0;
      border-radius: 8px 8px 0 0;
    }

    &:before {
      left: 6px;
    }

    &:after {
      right: 6px;
    }

    .center-line {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 2px;
      height: 177px;
      background-color: #E2E8F0;
      border-radius: 4px;
      margin-left: -1px;
      margin-top: -88px;
    }
  }
}

.loading {
  height: 100%;
  min-height: 100px;
  position: absolute;
  width: 100%;
  top: 0;
  background: rgb(213 213 213 / 50%);
  border-radius: 16px;
  z-index: 1;
}
</style>
